/**
 * CreateAdminFromSupervisorSection — إنشاء حساب يوزر لمشرف.
 * يدعم وضعين:
 *  1) "registered"  → اختيار موظف/مشرف مُدرَج مسبقاً عبر قائمة منسدلة قابلة للبحث (تعبئة تلقائية لكل الحقول
 *     بما فيها الرقم الوظيفي والمسمى الوظيفي).
 *  2) "unregistered" → إدخال يدوي كامل لمستخدم غير مدرج.
 * في الحالتين يتم إرسال بريد تعيين كلمة المرور (Token صالح 24 ساعة).
 * بعد النجاح يظهر Dialog يسأل: إنشاء حساب آخر أو إنهاء.
 */
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  UserPlus, Mail, CheckCircle, User, Phone, Loader2,
  ChevronsUpDown, Check, Search, UserCheck, UserX, IdCard, Briefcase,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

export default function CreateAdminFromSupervisorSection({
  language,
  emailSentInfo,
  loadingSupervisors,
  registeredSupervisors,
  setActiveSection,
  selectedSupervisor,
  handleSupervisorSelect,
  newAdmin,
  setNewAdmin,
  creatingAdmin,
  handleCreateAdmin,
  successDialog,
  onCreateAnother,
  onFinishCreate,
  jobTitles = [],
}) {
  // وضع الإنشاء: "registered" (موظف مُدرَج) أو "unregistered" (إدخال يدوي)
  const [mode, setMode] = useState('registered');
  const [comboOpen, setComboOpen] = useState(false);
  // tracks whether the admin has manually edited the username field;
  // until they do, the username mirrors the employee_id as they type it.
  const [usernameTouched, setUsernameTouched] = useState(false);

  // قائمة الموظفين المتاحين (لم يُنشأ لهم حساب يوزر بعد)
  const supervisorsFiltered = useMemo(() => registeredSupervisors || [], [registeredSupervisors]);

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    // مسح البيانات عند تبديل الوضع لتجنّب اختلاط الإدخالات
    handleSupervisorSelect(null);
    setNewAdmin({
      username: '',
      email: '',
      phone: '',
      first_name: '',
      last_name: '',
      employee_id: '',
      job_title_id: '',
      job_title_ar: '',
      job_title_en: '',
      preferred_language: 'ar',
    });
  };

  // عند اختيار موظف من الـ combobox، نمرّر الـ id للأب لتعبئة الحقول تلقائياً
  const onPickSupervisor = (supervisor) => {
    handleSupervisorSelect(supervisor.id);
    setComboOpen(false);
  };

  // في وضع "unregistered" نولّد اسم مستخدم تلقائياً عند اكتمال الاسم
  const autoGenerateUsername = () => {
    const first = (newAdmin.first_name || '').toLowerCase().replace(/\s+/g, '');
    const last = (newAdmin.last_name || '').toLowerCase().replace(/\s+/g, '');
    if (!first || !last) {
      toast.error(language === 'ar' ? 'أدخل الاسم الأول والأخير أولاً' : 'Enter first and last name first');
      return;
    }
    const random = Math.floor(Math.random() * 999) + 1;
    setNewAdmin({ ...newAdmin, username: `${first}.${last}${random}` });
  };

  // متى تُعرض حقول الإدخال؟
  // - "registered": فقط بعد اختيار موظف من القائمة (selectedSupervisor != null)
  // - "unregistered": دائماً
  const showFields = mode === 'unregistered' || selectedSupervisor;

  // تسمية زر الـ combobox
  const comboboxLabel = selectedSupervisor
    ? `${selectedSupervisor.first_name || ''} ${selectedSupervisor.last_name || ''} — ${selectedSupervisor.email || ''}`.trim()
    : (language === 'ar'
        ? 'اختر موظف/مشرف مُدرَج…'
        : 'Select a registered employee…');

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6"
        data-testid="create-admin-section"
      >
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-blue-400" />
          {language === 'ar' ? 'إنشاء حساب يوزر' : 'Create User Account'}
        </h2>

        {/* Fallback Info Box (when email failed and we want to expose links) */}
        {emailSentInfo && !emailSentInfo.success && emailSentInfo.password_setup_link && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl border bg-amber-500/10 border-amber-500/30"
          >
            <div className="flex items-start gap-3">
              <Mail className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-400">{emailSentInfo.message}</p>
                <p className="text-sm text-[#808080] mt-1">
                  {language === 'ar' ? 'البريد الإلكتروني: ' : 'Email: '}{emailSentInfo.email}
                </p>
                <div className="mt-4 space-y-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-[#808080] mb-1">
                      {language === 'ar' ? 'رابط إعادة تعيين كلمة المرور:' : 'Password setup link:'}
                    </p>
                    <p className="text-xs text-blue-400 break-all font-mono" dir="ltr">
                      {emailSentInfo.password_setup_link}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ===== Mode Toggle: Registered vs Unregistered ===== */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="create-mode-toggle">
          <button
            type="button"
            onClick={() => switchMode('registered')}
            className={cn(
              'rounded-2xl border p-4 flex items-start gap-3 transition-all text-right',
              mode === 'registered'
                ? 'bg-emerald-500/10 border-emerald-500/40 ring-2 ring-emerald-500/30'
                : 'bg-white/[0.02] border-white/10 hover:border-white/20'
            )}
            data-testid="mode-registered-btn"
          >
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              mode === 'registered' ? 'bg-emerald-500/20' : 'bg-white/5'
            )}>
              <UserCheck className={cn('h-5 w-5', mode === 'registered' ? 'text-emerald-400' : 'text-[#808080]')} />
            </div>
            <div className="flex-1">
              <p className={cn('font-semibold mb-1', mode === 'registered' ? 'text-emerald-300' : 'text-white')}>
                {language === 'ar' ? 'موظف/مشرف مُدرَج مسبقاً' : 'Registered employee'}
              </p>
              <p className="text-xs text-[#808080] leading-relaxed">
                {language === 'ar'
                  ? 'اختر من قائمة المدرجين عبر الهوية — يتم تعبئة الحقول والرقم الوظيفي تلقائياً.'
                  : 'Pick from supervisors registered via Emirates ID — fields & employee_id auto-fill.'}
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => switchMode('unregistered')}
            className={cn(
              'rounded-2xl border p-4 flex items-start gap-3 transition-all text-right',
              mode === 'unregistered'
                ? 'bg-blue-500/10 border-blue-500/40 ring-2 ring-blue-500/30'
                : 'bg-white/[0.02] border-white/10 hover:border-white/20'
            )}
            data-testid="mode-unregistered-btn"
          >
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              mode === 'unregistered' ? 'bg-blue-500/20' : 'bg-white/5'
            )}>
              <UserX className={cn('h-5 w-5', mode === 'unregistered' ? 'text-blue-400' : 'text-[#808080]')} />
            </div>
            <div className="flex-1">
              <p className={cn('font-semibold mb-1', mode === 'unregistered' ? 'text-blue-300' : 'text-white')}>
                {language === 'ar' ? 'مستخدم غير مدرج' : 'Unregistered user'}
              </p>
              <p className="text-xs text-[#808080] leading-relaxed">
                {language === 'ar'
                  ? 'إدخال يدوي كامل لاسم وبريد ورقم هاتف مستخدم جديد.'
                  : 'Manually enter the full name, email, and phone for a new user.'}
              </p>
            </div>
          </button>
        </div>

        {/* ===== Registered Mode: Searchable Combobox ===== */}
        {mode === 'registered' && (
          <div className="mb-6 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
            <label className="block text-sm text-emerald-300 font-medium mb-3">
              {language === 'ar' ? 'ابحث واختر الموظف' : 'Search and pick employee'} *
            </label>

            {loadingSupervisors ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
                <span className="text-[#808080] text-sm mr-2">
                  {language === 'ar' ? 'جاري التحميل…' : 'Loading…'}
                </span>
              </div>
            ) : supervisorsFiltered.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-[#808080] text-sm mb-3">
                  {language === 'ar'
                    ? 'لا يوجد موظفون/مشرفون مُدرَجون متاحون (الكل يملك حساباً).'
                    : 'No registered employees available (all have accounts).'}
                </p>
                <Button
                  onClick={() => setActiveSection('add-admin-id')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="goto-add-admin-id-btn"
                >
                  <UserPlus className="h-4 w-4 me-2" />
                  {language === 'ar' ? 'إدراج مشرف جديد' : 'Add New Supervisor'}
                </Button>
              </div>
            ) : (
              <Popover open={comboOpen} onOpenChange={setComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboOpen}
                    className="w-full h-12 bg-white/5 border-white/10 text-white hover:bg-white/10 justify-between"
                    data-testid="supervisor-combobox-trigger"
                  >
                    <span className={cn('truncate text-right', !selectedSupervisor && 'text-[#808080]')}>
                      {comboboxLabel}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#1a1a2e] border-white/10 text-white"
                  align="start"
                >
                  <Command className="bg-transparent" filter={(value, search) => {
                    const sup = supervisorsFiltered.find((s) => s.id === value);
                    if (!sup) return 0;
                    const hay = `${sup.first_name || ''} ${sup.last_name || ''} ${sup.full_name_ar || ''} ${sup.email || ''} ${sup.phone || ''} ${sup.id_number || ''} ${sup.employee_id || ''}`.toLowerCase();
                    return hay.includes(search.toLowerCase()) ? 1 : 0;
                  }}>
                    <CommandInput
                      placeholder={language === 'ar' ? 'ابحث بالاسم أو البريد أو الرقم الوظيفي…' : 'Search by name, email or employee #…'}
                      className="text-white placeholder:text-[#606060]"
                      data-testid="supervisor-combobox-search"
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="py-3 text-center text-sm text-[#808080] flex items-center justify-center gap-2">
                          <Search className="h-4 w-4" />
                          {language === 'ar' ? 'لا توجد نتائج' : 'No results'}
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {supervisorsFiltered.map((sup) => {
                          const isSelected = selectedSupervisor?.id === sup.id;
                          return (
                            <CommandItem
                              key={sup.id}
                              value={sup.id}
                              onSelect={() => onPickSupervisor(sup)}
                              className="text-white aria-selected:bg-emerald-500/20 aria-selected:text-white data-[selected=true]:bg-emerald-500/20 cursor-pointer"
                              data-testid={`supervisor-option-${sup.id}`}
                            >
                              <Check className={cn('h-4 w-4', isSelected ? 'opacity-100 text-emerald-400' : 'opacity-0')} />
                              <div className="flex flex-col flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">
                                    {sup.full_name_ar || `${sup.first_name || ''} ${sup.last_name || ''}`.trim() || sup.email}
                                  </span>
                                  {sup.employee_id && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#d4af37]/20 text-[#d4af37] shrink-0">
                                      #{sup.employee_id}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-[#808080] truncate" dir="ltr">
                                  {sup.email}{sup.phone ? ` · ${sup.phone}` : ''}
                                </span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {selectedSupervisor && (
              <p className="text-xs text-emerald-400 mt-3 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" />
                {language === 'ar' ? 'تم اختيار الموظف — البيانات معبأة تلقائياً' : 'Employee picked — fields auto-filled'}
              </p>
            )}
          </div>
        )}

        {/* ===== Form Fields ===== */}
        {showFields && (
          <>
            {/* الرقم الوظيفي + المسمى - chips قراءة فقط في الوضع المسجل */}
            {mode === 'registered' && selectedSupervisor && (newAdmin.employee_id || newAdmin.job_title_ar) && (
              <div className="mb-5 flex flex-wrap gap-3" data-testid="readonly-employee-info">
                {newAdmin.employee_id && (
                  <div className="bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl px-4 py-2.5 flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-[#d4af37]" />
                    <span className="text-xs text-[#808080]">
                      {language === 'ar' ? 'الرقم الوظيفي:' : 'Employee ID:'}
                    </span>
                    <span className="text-sm font-bold text-[#d4af37]" dir="ltr">{newAdmin.employee_id}</span>
                  </div>
                )}
                {newAdmin.job_title_ar && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-2.5 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-400" />
                    <span className="text-xs text-[#808080]">
                      {language === 'ar' ? 'المسمى الوظيفي:' : 'Job title:'}
                    </span>
                    <span className="text-sm font-bold text-blue-300">{newAdmin.job_title_ar}</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* اسم المستخدم */}
              <div className="space-y-2">
                <label className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'اسم المستخدم' : 'Username'} *
                </label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                  <Input
                    value={newAdmin.username}
                    onChange={(e) => {
                      setNewAdmin({ ...newAdmin, username: e.target.value });
                      setUsernameTouched(true);
                    }}
                    placeholder={
                      language === 'ar'
                        ? 'يُسحب من الرقم الوظيفي تلقائياً'
                        : 'Pulled from Employee ID automatically'
                    }
                    className="bg-white/5 border-white/10 text-white pr-10 h-12 focus:border-blue-500"
                    dir="ltr"
                    data-testid="input-username"
                  />
                </div>
                {mode === 'registered' ? (
                  <p className="text-xs text-emerald-400">
                    {language === 'ar'
                      ? 'تمت تعبئة اسم المستخدم بالرقم الوظيفي — يمكنك تعديله'
                      : 'Filled with the Employee ID — you can edit it'}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const empId = (newAdmin.employee_id || '').trim();
                      if (empId) {
                        setNewAdmin({ ...newAdmin, username: empId });
                        setUsernameTouched(false);
                      } else {
                        autoGenerateUsername();
                      }
                    }}
                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                    data-testid="auto-gen-username-btn"
                  >
                    {newAdmin.employee_id
                      ? (language === 'ar' ? 'استخدم الرقم الوظيفي كاسم مستخدم' : 'Use Employee ID as username')
                      : (language === 'ar' ? 'توليد اسم مستخدم تلقائياً' : 'Auto-generate username')}
                  </button>
                )}
              </div>

              {/* البريد الإلكتروني */}
              <div className="space-y-2">
                <label className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *
                </label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                  <Input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    placeholder="admin@example.com"
                    className="bg-white/5 border-white/10 text-white pr-10 h-12 focus:border-blue-500"
                    dir="ltr"
                    data-testid="input-email"
                  />
                </div>
                <p className="text-xs text-[#606060]">
                  {language === 'ar'
                    ? 'سيتم إرسال رابط تعيين كلمة المرور لهذا البريد'
                    : 'Password setup link will be sent here'}
                </p>
              </div>

              {/* رقم الهاتف */}
              <div className="space-y-2">
                <label className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'رقم الهاتف' : 'Phone'}
                </label>
                <div className="flex gap-2">
                  <div className="bg-white/5 border border-white/10 rounded-xl h-12 px-4 flex items-center gap-2 text-white">
                    <span className="text-lg">🇦🇪</span>
                    <span className="text-sm text-[#808080]">+971</span>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                    <Input
                      type="tel"
                      value={(newAdmin.phone || '').replace(/^\+971/, '')}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                        setNewAdmin({ ...newAdmin, phone: digits ? `+971${digits}` : '' });
                      }}
                      placeholder="50 123 4567"
                      className="bg-white/5 border-white/10 text-white pr-10 h-12 rounded-xl focus:border-blue-500"
                      dir="ltr"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              {/* الرقم الوظيفي (قابل للتعديل في وضع unregistered) */}
              {mode === 'unregistered' && (
                <div className="space-y-2">
                  <label className="text-sm text-[#71717A]">
                    {language === 'ar' ? 'الرقم الوظيفي' : 'Employee ID'}
                  </label>
                  <div className="relative">
                    <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                    <Input
                      value={newAdmin.employee_id || ''}
                      onChange={(e) => {
                        const newEmpId = e.target.value;
                        setNewAdmin({
                          ...newAdmin,
                          employee_id: newEmpId,
                          // Mirror into username until the admin manually edits the username field
                          ...(usernameTouched ? {} : { username: newEmpId.trim() }),
                        });
                      }}
                      placeholder={language === 'ar' ? 'اختياري — مثال: EMP001' : 'Optional — e.g., EMP001'}
                      className="bg-white/5 border-white/10 text-white pr-10 h-12 focus:border-blue-500"
                      dir="ltr"
                      data-testid="input-employee-id"
                    />
                  </div>
                </div>
              )}

              {/* المسمى الوظيفي - dropdown مرتبط بصفحة إدارة المسميات الوظيفية */}
              {mode === 'unregistered' && (
                <div className="space-y-2">
                  <label className="text-sm text-[#71717A]">
                    {language === 'ar' ? 'المسمى الوظيفي *' : 'Job Title *'}
                  </label>
                  <Select
                    value={newAdmin.job_title_id || undefined}
                    onValueChange={(v) => {
                      const picked = jobTitles.find((t) => t.id === v);
                      setNewAdmin({
                        ...newAdmin,
                        job_title_id: v,
                        job_title_ar: picked?.name_ar || '',
                        job_title_en: picked?.name_en || '',
                      });
                    }}
                  >
                    <SelectTrigger
                      className="bg-white/5 border-white/10 text-white h-12 focus:ring-2 focus:ring-blue-500/50"
                      data-testid="create-admin-job-title-trigger"
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-[#71717A]" />
                        <SelectValue
                          placeholder={
                            language === 'ar'
                              ? (jobTitles.length === 0 ? 'لا توجد مسميات — أضِفها من إدارة المسميات' : 'اختر المسمى الوظيفي')
                              : (jobTitles.length === 0 ? 'No titles — add them from Job Titles management' : 'Select job title')
                          }
                        />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                      {jobTitles.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-[#A1A1AA] text-center">
                          {language === 'ar'
                            ? 'لا توجد مسميات وظيفية. أضِفها من صفحة "المسميات الوظيفية".'
                            : 'No job titles available. Add some from "Job Titles" management.'}
                        </div>
                      ) : (
                        jobTitles.map((title) => (
                          <SelectItem
                            key={title.id}
                            value={title.id}
                            className="text-white focus:bg-blue-500/20 focus:text-white data-[highlighted]:bg-blue-500/20 data-[highlighted]:text-white"
                          >
                            {language === 'ar' ? title.name_ar : title.name_en}
                            <span className="text-[10px] text-[#71717A] ms-2" dir="ltr">
                              ({language === 'ar' ? title.name_en : title.name_ar})
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#606060]">
                    {language === 'ar'
                      ? 'القائمة تُسحب من "المسميات الوظيفية" في لوحة التحكم.'
                      : 'List is pulled from "Job Titles" management in control panel.'}
                  </p>
                </div>
              )}

              {/* الاسم الأول */}
              <div className="space-y-2">
                <label className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'الاسم الأول' : 'First Name'} *
                </label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                  <Input
                    value={newAdmin.first_name}
                    onChange={(e) => setNewAdmin({ ...newAdmin, first_name: e.target.value })}
                    placeholder={language === 'ar' ? 'الاسم الأول' : 'First name'}
                    className="bg-white/5 border-white/10 text-white pr-10 h-12 focus:border-blue-500"
                    data-testid="input-first-name"
                  />
                </div>
              </div>

              {/* الاسم الأخير */}
              <div className="space-y-2">
                <label className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'الاسم الأخير' : 'Last Name'} *
                </label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
                  <Input
                    value={newAdmin.last_name}
                    onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
                    placeholder={language === 'ar' ? 'الاسم الأخير' : 'Last name'}
                    className="bg-white/5 border-white/10 text-white pr-10 h-12 focus:border-blue-500"
                    data-testid="input-last-name"
                  />
                </div>
              </div>
            </div>

            {/* Preferred Language — used for the welcome email */}
            <div className="mt-6">
              <label className="text-white text-sm font-medium mb-2 block">
                {language === 'ar' ? 'اللغة المفضلة (للإيميلات)' : 'Preferred Language (for emails)'}
                <span className="text-red-400 ml-1">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { code: 'ar', labelAr: '🇸🇦 العربية', labelEn: '🇸🇦 العربية' },
                  { code: 'en', labelAr: '🇬🇧 English', labelEn: '🇬🇧 English' },
                ].map((opt) => {
                  const active = (newAdmin.preferred_language || 'ar') === opt.code;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      onClick={() => setNewAdmin({ ...newAdmin, preferred_language: opt.code })}
                      data-testid={`lang-option-${opt.code}`}
                      className={`h-11 rounded-xl border text-sm font-medium transition-all ${
                        active
                          ? 'bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]'
                          : 'bg-white/5 border-white/10 text-[#808080] hover:bg-white/10'
                      }`}
                    >
                      {language === 'ar' ? opt.labelAr : opt.labelEn}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#808080] mt-2">
                {language === 'ar'
                  ? 'بناءً على هذا الاختيار، سيُرسل بريد التفعيل وكل رسائل النظام لاحقاً باللغة المختارة.'
                  : 'The activation email and future system emails will be sent in this language.'}
              </p>
            </div>

            {/* Info Box */}
            <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4" data-testid="create-admin-info-box">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium text-sm">
                    {language === 'ar' ? 'كيف يعمل إنشاء حساب المشرف؟' : 'How does it work?'}
                  </p>
                  <ul className="text-xs text-[#808080] mt-2 space-y-1 list-disc list-inside">
                    <li>
                      {language === 'ar'
                        ? 'يتم إنشاء حساب جديد بحالة "بانتظار التفعيل"'
                        : 'A new account is created in "Pending Activation" state'}
                    </li>
                    <li>
                      {language === 'ar'
                        ? 'يُرسَل بريد إلكتروني يحتوي على اسم المستخدم ورابط تعيين كلمة المرور'
                        : 'An email with the username + password setup link is sent'}
                    </li>
                    <li>
                      {language === 'ar'
                        ? 'الرابط صالح لمدة 24 ساعة فقط — بعدها تلزم إعادة الإرسال'
                        : 'The link is valid for 24 hours only — resend if it expires'}
                    </li>
                    <li>
                      {language === 'ar'
                        ? 'يظهر المستخدم في "متابعة الجديد" حتى يُكمل تعيين كلمة المرور'
                        : 'User appears in "Pending Activation" until password is set'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* زر الإنشاء */}
            <div className="mt-8">
              <Button
                onClick={handleCreateAdmin}
                disabled={
                  creatingAdmin
                  || !newAdmin.username
                  || !newAdmin.email
                  || !newAdmin.first_name
                  || !newAdmin.last_name
                }
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg font-semibold rounded-xl disabled:opacity-50"
                data-testid="btn-submit-create-admin"
              >
                {creatingAdmin ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin ml-2" />
                    {language === 'ar' ? 'جاري الإنشاء…' : 'Creating…'}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5 ml-2" />
                    {language === 'ar' ? 'إنشاء حساب وإرسال البريد' : 'Create Account & Send Email'}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </motion.div>

      {/* ===== Success Dialog: "Create another or done?" ===== */}
      <Dialog
        open={!!successDialog}
        onOpenChange={(open) => { if (!open) onFinishCreate?.(); }}
      >
        <DialogContent
          className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md"
          dir="rtl"
          data-testid="create-admin-success-dialog"
        >
          <DialogHeader>
            <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <DialogTitle className="text-center text-lg">
              {language === 'ar' ? 'تم إنشاء الحساب بنجاح' : 'Account created successfully'}
            </DialogTitle>
            <DialogDescription className="text-center text-[#A1A1AA] mt-1 space-y-1">
              {successDialog?.emailSent ? (
                <span>
                  {language === 'ar'
                    ? 'تم إرسال رابط تعيين كلمة المرور إلى البريد الإلكتروني.'
                    : 'Password setup link has been emailed.'}
                </span>
              ) : (
                <span>
                  {language === 'ar'
                    ? 'تم إنشاء الحساب لكن فشل إرسال البريد — استخدم الرابط الاحتياطي.'
                    : 'Account created but email failed — use the fallback link.'}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {successDialog && (
            <div className="space-y-2 bg-white/[0.03] rounded-xl p-3 border border-white/10">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#808080]">{language === 'ar' ? 'اسم المستخدم' : 'Username'}</span>
                <span className="text-[#d4af37] font-mono" dir="ltr">{successDialog.username}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#808080]">{language === 'ar' ? 'البريد' : 'Email'}</span>
                <span className="text-white" dir="ltr">{successDialog.email}</span>
              </div>
              {!successDialog.emailSent && successDialog.passwordSetupLink && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-xs text-[#808080] mb-1">
                    {language === 'ar' ? 'رابط تعيين كلمة المرور (يدوي):' : 'Manual password link:'}
                  </p>
                  <p className="text-xs text-blue-400 break-all font-mono" dir="ltr">
                    {successDialog.passwordSetupLink}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="!flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={onFinishCreate}
              className="flex-1 border-white/15 text-white hover:bg-white/10"
              data-testid="dialog-finish-btn"
            >
              {language === 'ar' ? 'تم' : 'Done'}
            </Button>
            <Button
              onClick={onCreateAnother}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="dialog-create-another-btn"
            >
              <UserPlus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'إنشاء حساب آخر' : 'Create another'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
